import React, { ReactNode, useState, useCallback } from "react";

type AlertStatus = "success" | "info" | "warning" | "error";

interface Alert {
  title: string;
  message: string;
  status?: AlertStatus;
}

interface AlertContextValue {
  alert: Alert | null;
  addAlert: (title: string, message: string, status?: AlertStatus) => void;
  removeAlert: () => void;
}

export const AlertContext = React.createContext<AlertContextValue>({
  alert: null,
  addAlert: () => {},
  removeAlert: () => {},
});

export default function AlertProvider({ children }: { children: ReactNode }) {
  const [alert, setAlert] = useState<Alert | null>(null);

  const removeAlert = () => setAlert(null);

  const addAlert = (title: string, message: string, status?: AlertStatus) =>
    setAlert({ title, message, status });

  const contextValue: AlertContextValue = {
    alert,
    addAlert: useCallback(
      (title: string, message: string, status?: AlertStatus) =>
        addAlert(title, message, status),
      [],
    ),
    removeAlert: useCallback(() => removeAlert(), []),
  };

  return (
    <AlertContext.Provider value={contextValue}>
      {children}
    </AlertContext.Provider>
  );
}
